import React, { Component } from 'react';
import { ServiceAreaMap, CountyPolygons as countyPolygons } from '@capcenter/shared-service-map';
import { Colors, getCounties, getBooleanFeatureFlag } from '@capcenter/shared';
import { AddressSearch, Icon } from '@capcenter/shared-legacy';
import Breakpoint from './Breakpoint';

class ServiceArea extends Component {
	constructor(props) {
		super(props);

		this.state = {
			polygons: [],
			loading: true,
			counties: [],
			currentCounty: undefined,
		};
	}

	async componentDidMount() {

		getCounties().then(counties => {
			if (typeof counties !== 'string') {
				const { polygons, mapCenter, loading } = this.getInitialState(counties);

				polygons.sort((a, b) => a.extremes.size - b.extremes.size);

				this.setState({ polygons, mapCenter, loading });
			} else {
				this.printError(counties);
			}
		});
	}

	/** Function that modifies and updates 'extremes' type objects.
	 *
	 *  These extremes objects serve to keep a running min/max longitude/latitude, center, and size of coordinates.
	 *  The min/max longitude/latitudes are solely use to calculate the center and size, except for one exception where it is used in ServiceAreaMap.
	 *
	 *  These 'extremes' objects are used to sort the polygons by size, and in the ServiceAreaMap to center the tooltip
	 */
	lookForCenter = (extremes, lat, lng) => {
		let newExtremes = JSON.parse(JSON.stringify(extremes));

		if (lat > extremes.maxLatitude || extremes.maxLatitude === 0) {
			newExtremes.maxLatitude = lat;
		}
		if (lat < extremes.minLatitude || extremes.minLatitude === 0) {
			newExtremes.minLatitude = lat;
		}
		if (lng > extremes.maxLongitude || extremes.maxLongitude === 0) {
			newExtremes.maxLongitude = lng;
		}
		if (lng < extremes.minLongitude || extremes.minLongitude === 0) {
			newExtremes.minLongitude = lng;
		}

		newExtremes.center = {
			lat: newExtremes.minLatitude + (newExtremes.maxLatitude - newExtremes.minLatitude) / 2,
			lng: newExtremes.minLongitude + (newExtremes.maxLongitude - newExtremes.minLongitude) / 2,
		};
		newExtremes.size =
			(newExtremes.maxLatitude - newExtremes.minLatitude) * (newExtremes.maxLongitude - newExtremes.minLongitude);

		return newExtremes;
	};

	polygonMatchesCounty = (countyPolygon, capcenterCounty) => {
		const altNameArray = countyPolygon.properties.Geographic_Name.split(',');
		const altName = altNameArray[0];

		return (
			(countyPolygon.properties.name.toLowerCase() === capcenterCounty.CountyName.toLowerCase() ||
				altName.toLowerCase() === capcenterCounty.CountyName.toLowerCase()) &&
			countyPolygon.properties['State Abbr'].toLowerCase() === capcenterCounty.State.toLowerCase()
		);
	};

	getInitialState = capcenterCounties => {
		// initialize final polygon array
		let polygons = [];

		let allExtremes = {
			minLatitude: 0,
			maxLatitude: 0,
			minLongitude: 0,
			maxLongitude: 0,
			center: 0,
			size: 0,
		};

		const processCoordinates = geometry => {
			let coordinates = [];

			let extremes = {
				minLatitude: 0,
				maxLatitude: 0,
				minLongitude: 0,
				maxLongitude: 0,
				center: 0,
				size: 0,
			};

			// Processes countyPolygon geometries with single polygon coordinate array
			if (geometry.type === 'Polygon') {
				geometry.coordinates[0].forEach(coordinate => {
					const [currentLongitude, currentLatitude] = coordinate.map(singleCoordinate => parseFloat(singleCoordinate));

					extremes = this.lookForCenter(extremes, currentLatitude, currentLongitude);
					allExtremes = this.lookForCenter(allExtremes, currentLatitude, currentLongitude);

					coordinates.push({ lat: currentLatitude, lng: currentLongitude });
				});

				// Processes countyPolygon geometries with multiple polygon coordinate array
			} else if (geometry.type === 'MultiPolygon') {
				geometry.coordinates.forEach(coordinateArray => {
					let coordinatesWithExtremes = coordinateArray[0].map(coordinate => {
						const [currentLongitude, currentLatitude] = coordinate.map(singleCoordinate =>
							parseFloat(singleCoordinate),
						);

						extremes = this.lookForCenter(extremes, currentLatitude, currentLongitude);
						allExtremes = this.lookForCenter(allExtremes, currentLatitude, currentLongitude);

						return { lat: currentLatitude, lng: currentLongitude };
					});
					coordinates.push(coordinatesWithExtremes);
				});
			}

			return { coordinates, extremes };
		};

		countyPolygons.forEach(countyPolygon => {
			// Start check for missing capcenter county match
			let polygonExistsInCapcenterCounties = false;

			// Check each polygon against all counties in CapCenter database
			capcenterCounties.forEach((capcenterCounty, capcenterCountyIndex) => {

				// If the county and state do not match, do not continue
				if (!this.polygonMatchesCounty(countyPolygon, capcenterCounty)) return;

				polygonExistsInCapcenterCounties = true;

				if (countyPolygon.geometry.coordinates) {
					// processing flips the order of the coordinate tuples, and calculates the polygon extremes object
					const { coordinates, extremes } = processCoordinates(countyPolygon.geometry);

					// adds processed coordinates and polygon data as polygon object to final polygon array
					if (!Array.isArray(coordinates[0])) {
						polygons.push({
							county: capcenterCounty.CountyName,
							state: capcenterCounty.State,
							coords: coordinates,
							extremes: extremes,
							cData: capcenterCounty,
							cId: capcenterCounty.CountyName.replace(/\s/g, '') + capcenterCounty.State,
						});
					} else {
						// handling multiple coordinate arrays
						coordinates.forEach((coordinate, multiCoordIndex) => {
							polygons.push({
								county: capcenterCounty.CountyName,
								state: capcenterCounty.State,
								coords: coordinate,
								extremes: extremes,
								cData: capcenterCounty,
								cId: capcenterCounty.CountyName.replace(/\s/g, '') + capcenterCounty.State + multiCoordIndex,
							});
						});
					}
				}

			});

			// If there's not a match in the database, log it.
			if (polygonExistsInCapcenterCounties === false) {
				console.warn('County polygon - no match found in CC Database', countyPolygon);
			}
		});

		return { polygons, mapCenter: allExtremes.center, loading: false };
	};

	printError = error => {
		console.error(error);
	};

	setCounty = county => {
		this.setState({ currentCounty: county.countyId });
	};

	render() {
		return (

			<Breakpoint>
				{bProps => (
					<>
						<div
							style={{
								position: 'relative',
								width: '100%',
								height: bProps.size === 'xSmall' ? 250 : bProps.size === 'xLarge' ? 500 : 500,
								backgroundColor: '#E5E3DF',
							}}
						>
							{this.state.loading ? (
								<div
									style={{
										position: 'absolute',
										top: 0,
										left: 0,
										right: 0,
										bottom: 0,
										zIndex: 0,
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
									}}
								>
									<h2>
										<Icon icon="spinner" pulse /> &nbsp;Loading Data
									</h2>
								</div>
							) : (
								<ServiceAreaMap
									initialCenter={{
										lat: 32.5,
										lng: -81,
									}}
									locations={this.props.locations}
									zoom={5}
									polygons={this.state.polygons}
									setGoogle={() => this.setState({ loading: false })} // This allows address search to load after google mounts
									setId={id => this.setState({ currentCounty: id })}
									currentCounty={this.state.currentCounty}
								/>
							)}
						</div>

						<div
							style={{
								backgroundColor: Colors.gray350,
								height: bProps.size === 'xSmall' || bProps.size === 'small' || bProps.size === 'xLarge' ? 50 : 60,
							}}
						>
							<div className="container" style={{ padding: 5 }}>
								<div className="row d-flex align-items-center justify-content-center">
									<div className="col-7 col-lg-7 col-xl-6">
										<AddressSearch
											google={false}
											setCounty={this.setCounty}
											noPadding={true}
											log={false}
											placeholder="Enter Your Location"
										/>
									</div>
									<div className="col-5 col-lg-5 col-xl-6">
										<span className="badge" style={{ backgroundColor: Colors.red500, width: 15, height: 15 }}>
											&nbsp;&nbsp;
										</span>
										&nbsp; Realty & Mortgage
										<br className="d-block d-xl-none" />
										<span className="d-none d-xl-inline">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
										<span className="badge" style={{ backgroundColor: Colors.primary, width: 15, height: 15 }}>
											&nbsp;&nbsp;
										</span>
										&nbsp; Mortgage Only
									</div>
								</div>
							</div>
						</div>
					</>
				)}
			</Breakpoint>
		);
	}
}

export { ServiceArea }
